import { useCallback, useEffect, useState } from 'react';
import { keycloak, getValidToken } from './keycloak';

/**
 * React-side wrapper around the module-level Keycloak singleton. Exposes a
 * stable object so components don't import `./keycloak` directly, and a
 * `isAuthenticated` state that re-renders the consumer when the
 * authentication state changes (e.g. after `keycloak.login()` returns or
 * after a token-refresh error).
 */
export interface UseKeycloakAuth {
    isAuthenticated: boolean;
    isInitialized: boolean;
    /**
     * Trigger Keycloak login. Optional `idpHint` (e.g. 'google') adds the
     * `kc_idp_hint` query param so Keycloak forwards the user straight
     * to that IdP instead of the realm login page. Optional `returnTo`
     * is stashed in sessionStorage so the callback page can navigate
     * the user back to the page they were originally trying to reach
     * (instead of the default /work-order landing).
     */
    login: (idpHint?: string, returnTo?: string) => void;
    register: (idpHint?: string) => void;
    logout: (redirectUri?: string) => void;
    getToken: (minValidity?: number) => Promise<string | null>;
    tokenParsed: Keycloak.KeycloakTokenParsed | undefined;
}

const RETURN_TO_KEY = 'autotrack_post_login_route';

const CALLBACK_PATH = '/auth/callback';

const buildRedirectUri = (): string => {
    return window.location.origin + CALLBACK_PATH;
};

export function useKeycloakAuth(): UseKeycloakAuth {
    // Sync with the singleton via state so React re-renders on auth changes.
    // (We avoid a context here because the singleton is already the source
    // of truth and a context would only add a layer of indirection.)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        keycloak.authenticated ?? false
    );
    const [tokenParsed, setTokenParsed] = useState<
        Keycloak.KeycloakTokenParsed | undefined
    >(keycloak.tokenParsed);

    useEffect(() => {
        const onAuthChange = () => {
            setIsAuthenticated(Boolean(keycloak.authenticated));
            setTokenParsed(keycloak.tokenParsed);
        };
        // keycloak-js fires these on init, login, logout, token refresh.
        keycloak.onAuthSuccess = onAuthChange;
        keycloak.onAuthLogout = onAuthChange;
        keycloak.onAuthRefreshSuccess = onAuthChange;
        keycloak.onAuthRefreshError = onAuthChange;
        keycloak.onTokenExpired = onAuthChange;

        return () => {
            // Detach handlers on unmount to avoid leaking listeners across
            // re-mounts in dev (HMR, route changes that re-mount the root).
            keycloak.onAuthSuccess = undefined;
            keycloak.onAuthLogout = undefined;
            keycloak.onAuthRefreshSuccess = undefined;
            keycloak.onAuthRefreshError = undefined;
            keycloak.onTokenExpired = undefined;
        };
    }, []);

    const login = useCallback((idpHint?: string, returnTo?: string) => {
        // `idpHint` becomes the `kc_idp_hint` query param on Keycloak's
        // authorization endpoint, which tells Keycloak to forward the user
        // straight to that identity provider's login UI instead of showing
        // the realm login page. Used to deep-link "Continue with Google"
        // to the Google IdP broker (no extra click on Keycloak's side).
        if (returnTo) {
            // Persist the originally-requested URL across the Keycloak
            // round-trip. The callback page reads this and navigates the
            // user back to it once authentication completes. We use
            // sessionStorage so it dies with the tab and can't be replayed
            // by a stale tab opened long after the user gave up.
            sessionStorage.setItem(RETURN_TO_KEY, returnTo);
        }
        void keycloak.login({
            redirectUri: buildRedirectUri(),
            ...(idpHint ? { idpHint } : {}),
        });
    }, []);

    const register = useCallback((idpHint?: string) => {
        void keycloak.register({
            redirectUri: buildRedirectUri(),
            ...(idpHint ? { idpHint } : {}),
        });
    }, []);

    const logout = useCallback((redirectUri?: string) => {
        void keycloak.logout({
            redirectUri: redirectUri ?? window.location.origin + '/login',
        });
    }, []);

    const getToken = useCallback(
        (minValidity: number = 30) => getValidToken(minValidity),
        []
    );

    return {
        // `index.tsx`'s bootstrap() awaits `keycloak.init(...)` before
        // rendering <Root />, so by the time any component mounts, init
        // has resolved and `keycloak.authenticated` is meaningful. We
        // expose `isInitialized` anyway in case a component is mounted
        // before the singleton finished init (e.g. during a test).
        isAuthenticated,
        isInitialized: keycloak.authenticated !== undefined,
        login,
        register,
        logout,
        getToken,
        tokenParsed,
    };
}
